import { Flex, Box, Menu, Portal } from '@chakra-ui/react';
import { LuChevronLeft, LuChevronRight, LuPencil, LuGlobe, LuCheck } from 'react-icons/lu';
import type { MenuTheme, StoreInfo } from '../../api/menus';
import { EDIT_HOVER, EDIT_TRANSITION, NATIVE_LANGUAGE_NAMES } from './types';
import InlineEdit from './InlineEdit';

interface MenuHeaderProps {
  name: string;
  description: string | null;
  theme: MenuTheme;
  storeInfo?: StoreInfo;
  isEditMode: boolean;
  showCategoryLanding: boolean;
  hasHeaderBanner: boolean;
  hasManyCategories: boolean;
  availableLanguages: string[];
  currentLanguage: string;
  vs: Record<string, string>;
  onLanguageChange?: (lang: string) => void;
  onTitleClick?: () => void;
  onStoreInfoClick?: () => void;
  onBack: () => void;
  onShowStoreInfo: () => void;
  onQuickEditTitle?: (name: string) => void;
  onQuickEditDescription?: (description: string) => void;
}

const MenuHeader = ({
  name,
  description,
  theme: mt,
  storeInfo,
  isEditMode,
  showCategoryLanding,
  hasHeaderBanner,
  hasManyCategories,
  availableLanguages,
  currentLanguage,
  vs,
  onLanguageChange,
  onTitleClick,
  onStoreInfoClick,
  onBack,
  onShowStoreInfo,
  onQuickEditTitle,
  onQuickEditDescription,
}: MenuHeaderProps) => {
  const hasStoreInfo = !!(storeInfo && (storeInfo.store_hours || storeInfo.address || storeInfo.wifi_password || storeInfo.instagram));
  const isQuickEditMode = !!onQuickEditTitle;

  const storeInfoLabel = hasStoreInfo ? (() => {
    const items = [
      storeInfo!.store_hours && vs.storeHours,
      storeInfo!.address && vs.address,
      storeInfo!.wifi_password && vs.wifi,
      storeInfo!.instagram && vs.instagram,
    ].filter(Boolean);
    return items.length > 0 ? <>{items.join(', ')} {'&'} {vs.moreInfo}</> : vs.storeInfo;
  })() : null;

  const renderTitleBlock = () => {
    // Edit mode - open settings modal
    if (isEditMode && onTitleClick) {
      return (
        <Flex
          alignItems="center"
          gap="8px"
          cursor="pointer"
          borderRadius="6px"
          mx="-8px"
          px="8px"
          py="4px"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="transparent"
          transition={EDIT_TRANSITION}
          _hover={EDIT_HOVER}
          onClick={onTitleClick}
          css={{ '&:hover .edit-icon': { opacity: 1 } }}
        >
          <Box flex="1">
            <Box fontSize="22px" fontWeight="600" color={mt.text_color} lineHeight="130%">{name}</Box>
            {description && (
              <Box fontSize="13px" color={mt.secondary_text_color} mt="4px" lineHeight="150%">{description}</Box>
            )}
          </Box>
          <Box className="edit-icon" opacity={0} transition="opacity 0.15s" color={mt.secondary_text_color} flexShrink={0}>
            <LuPencil size={14} />
          </Box>
        </Flex>
      );
    }

    // Quick edit mode - inline editable
    if (isQuickEditMode) {
      return (
        <>
          <InlineEdit
            value={name}
            onSave={onQuickEditTitle}
            fontSize="22px"
            fontWeight="600"
            color={mt.text_color}
            lineHeight="130%"
          />
          <Box mt="4px">
            <InlineEdit
              value={description || ''}
              onSave={onQuickEditDescription || (() => {})}
              fontSize="13px"
              color={mt.secondary_text_color}
              lineHeight="150%"
              multiline
              required={false}
              placeholder="Add description..."
            />
          </Box>
        </>
      );
    }

    // Normal view
    return (
      <>
        <Box fontSize="22px" fontWeight="600" color={mt.text_color} lineHeight="130%">{name}</Box>
        {description && (
          <Box fontSize="13px" color={mt.secondary_text_color} mt="4px" lineHeight="150%">{description}</Box>
        )}
      </>
    );
  };

  const renderDescriptionBelowBanner = () => {
    if (!hasHeaderBanner || !description) return null;

    if (isEditMode && onTitleClick) {
      return (
        <Flex
          alignItems="center"
          gap="8px"
          cursor="pointer"
          borderRadius="6px"
          mx="-8px"
          px="8px"
          py="4px"
          mt="4px"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="transparent"
          transition={EDIT_TRANSITION}
          _hover={EDIT_HOVER}
          onClick={onTitleClick}
          css={{ '&:hover .edit-icon': { opacity: 1 } }}
        >
          <Box flex="1" fontSize="13px" color={mt.secondary_text_color} lineHeight="150%">{description}</Box>
          <Box className="edit-icon" opacity={0} transition="opacity 0.15s" color={mt.secondary_text_color} flexShrink={0}>
            <LuPencil size={12} />
          </Box>
        </Flex>
      );
    }

    if (isQuickEditMode && onQuickEditDescription) {
      return (
        <Box mt="4px">
          <InlineEdit
            value={description}
            onSave={onQuickEditDescription}
            fontSize="13px"
            color={mt.secondary_text_color}
            lineHeight="150%"
            multiline
            required={false}
          />
        </Box>
      );
    }

    return (
      <Box fontSize="13px" color={mt.secondary_text_color} mt="4px" lineHeight="150%">{description}</Box>
    );
  };

  return (
    <Box px="20px" pt="16px" pb="12px">
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Box flex="1">
          {/* Back button */}
          {!showCategoryLanding && hasManyCategories && (
            <Flex
              alignItems="center"
              gap="4px"
              cursor="pointer"
              mb="8px"
              color={mt.secondary_text_color}
              _hover={{ color: mt.text_color }}
              onClick={onBack}
            >
              <LuChevronLeft size={16} />
              <Box fontSize="13px" fontWeight="500">{vs.back}</Box>
            </Flex>
          )}

          {/* Title & description on category landing */}
          {showCategoryLanding && (
            <>
              {!hasHeaderBanner && renderTitleBlock()}
              {renderDescriptionBelowBanner()}

              {/* Store info link */}
              {hasStoreInfo && (
                isEditMode && onStoreInfoClick ? (
                  <Flex
                    alignItems="center"
                    gap="6px"
                    mt="10px"
                    cursor="pointer"
                    color={mt.secondary_text_color}
                    borderRadius="6px"
                    mx="-8px"
                    px="8px"
                    py="4px"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="transparent"
                    transition={EDIT_TRANSITION}
                    _hover={EDIT_HOVER}
                    onClick={onStoreInfoClick}
                    css={{ '&:hover .edit-icon': { opacity: 1 } }}
                  >
                    <Box fontSize="13px" fontWeight="500" flex="1">{storeInfoLabel}</Box>
                    <Box className="edit-icon" opacity={0} transition="opacity 0.15s" flexShrink={0}>
                      <LuPencil size={12} />
                    </Box>
                  </Flex>
                ) : (
                  <Flex
                    alignItems="center"
                    gap="6px"
                    mt="10px"
                    cursor="pointer"
                    color={mt.secondary_text_color}
                    _hover={{ color: mt.text_color }}
                    onClick={onShowStoreInfo}
                  >
                    <Box fontSize="13px" fontWeight="500">{storeInfoLabel}</Box>
                    <LuChevronRight size={14} />
                  </Flex>
                )
              )}
            </>
          )}
        </Box>

        {/* Language switcher (non-banner) */}
        {!hasHeaderBanner && availableLanguages.length > 1 && onLanguageChange && (
          <Box flexShrink={0} ml="12px">
            <Menu.Root>
              <Menu.Trigger asChild>
                <Box
                  as="button"
                  display="flex"
                  alignItems="center"
                  gap="6px"
                  py="6px"
                  px="10px"
                  fontSize="13px"
                  fontWeight="400"
                  color={mt.secondary_text_color}
                  backgroundColor={mt.border_color}
                  borderRadius="6px"
                  cursor="pointer"
                  _hover={{ color: mt.text_color }}
                >
                  <LuGlobe style={{ width: 14, height: 14 }} />
                  {NATIVE_LANGUAGE_NAMES[currentLanguage] || currentLanguage.toUpperCase()}
                </Box>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner style={{ marginTop: '-4px' }}>
                  <Menu.Content
                    backgroundColor={mt.background_color}
                    borderWidth="1px"
                    borderColor={mt.border_color}
                    borderRadius="8px"
                    minWidth="160px"
                    p="4px"
                    css={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
                  >
                    {availableLanguages.map((code) => {
                      const label = NATIVE_LANGUAGE_NAMES[code] || code.toUpperCase();
                      return (
                        <Menu.Item
                          key={code}
                          value={code}
                          onClick={() => onLanguageChange(code)}
                          fontSize="13px"
                          fontWeight="400"
                          color={currentLanguage === code ? mt.text_color : mt.secondary_text_color}
                          backgroundColor={currentLanguage === code ? mt.border_color : 'transparent'}
                          borderRadius="6px"
                          px="10px"
                          py="7px"
                          _hover={{ backgroundColor: mt.border_color, color: mt.text_color }}
                          cursor="pointer"
                        >
                          <Flex alignItems="center" justifyContent="space-between" width="100%">
                            {label}
                            {currentLanguage === code && <LuCheck style={{ width: 14, height: 14 }} />}
                          </Flex>
                        </Menu.Item>
                      );
                    })}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default MenuHeader;
